import { useMemo } from 'react';

import { VFlex } from '../VFlex';
import { VGrid } from '../VGrid';
import { VIconButton } from '../VIconButton';
import { useVTheme } from '../VThemeProvider';
import { useVToast } from '../VToast';
import { VVirtualScroll } from '../VVirtualScroll';
import { ContentCopy } from '../asset';
import { joinCn } from '../util';
import { TABLE_ROW_SIZE_HEIGHT } from './constant';
import { useVBaseTableContext } from './hook';
import style from './style';

const TableBodyLoadingSlot = () => {
  const { renderLoadingData } = useVBaseTableContext();
  return <>{renderLoadingData?.() ?? null}</>;
};

const TableBodyEmptySlot = () => {
  const { renderEmptyData } = useVBaseTableContext();
  return <>{renderEmptyData?.() ?? null}</>;
};

const TableBodyScrollSlot = () => {
  const {
    cellRenderer,
    colsWidth,
    columnList,
    columnStyleMap,
    copyableColumnMap,
    data,
    eventEmitter,
    $tbody,
    rowClassName,
    rowSize,
  } = useVBaseTableContext();
  const { themeName } = useVTheme();
  const { addToast } = useVToast();
  const rowHeight = useMemo(
    () => TABLE_ROW_SIZE_HEIGHT[rowSize ?? 'md'],
    [rowSize],
  );
  const twStyle = style({ rowSize, themeName });

  const onCopy = ({
    cell,
    column,
    row,
  }: {
    cell: any;
    column: string;
    row: any;
  }) => {
    navigator.clipboard.writeText(cell).then(() => {
      addToast({
        autoClose: true,
        duration: 1000,
        message: `Copied '${cell}'`,
        variant: 'info',
      });
    });
    eventEmitter.emit('copy', { cell, column, row });
  };

  return (
    <VVirtualScroll
      ref={$tbody}
      item={{
        gap: 0,
        height: rowHeight + 1,
        list: data,
      }}
      className="w-full overflow-x-hidden!"
      data-testid="v-inner-base-table-tbody"
      renderItem={row => (
        <VGrid
          role="row"
          className={joinCn(
            twStyle.vBaseTableTbodyTr,
            typeof rowClassName === 'function'
              ? rowClassName(row)
              : rowClassName,
          )}
          cols={columnList.length}
          colsWidth={colsWidth}
          rows={1}
          rowsHeight={[`${rowHeight - 1}px`]}
          data-id={row.id}
          onClick={e => {
            eventEmitter.emit('rowClick', { nativeEvent: e, row });
          }}
        >
          {columnList.map(col => {
            const cell = row[col];
            return (
              <VFlex
                key={col}
                aria-label={col as string}
                role="cell"
                className={joinCn(
                  twStyle.vBaseTableTd,
                  columnStyleMap[col]?.className,
                )}
                style={columnStyleMap[col]?.style}
                items="center"
                gap={0.25}
                data-testid="v-inner-base-table-td"
                onClick={e => {
                  eventEmitter.emit('cellClick', {
                    cell,
                    column: col,
                    nativeEvent: e,
                    row,
                  });
                }}
                onDoubleClick={e => {
                  eventEmitter.emit('doubleClick', {
                    cell,
                    column: col,
                    nativeEvent: e,
                    row,
                  });
                }}
              >
                <div className="w-full flex-1 overflow-hidden text-ellipsis whitespace-nowrap">
                  {typeof cellRenderer?.[col] === 'function'
                    ? cellRenderer?.[col](cell, col, row)
                    : cell}
                </div>
                {copyableColumnMap?.[col] && (
                  <VIconButton
                    Icon={ContentCopy}
                    size="xs"
                    variant="roundPrimary"
                    onClick={e => {
                      e.stopPropagation();
                      onCopy({ cell, column: col, row });
                    }}
                  />
                )}
              </VFlex>
            );
          })}
        </VGrid>
      )}
    />
  );
};

const VInnerBaseTableTbody = () => {
  const { data, loading } = useVBaseTableContext();
  const hasData = data.length > 0;

  return (
    <div className="h-full w-fit min-w-full overflow-hidden">
      {loading && <TableBodyLoadingSlot />}
      {!loading && hasData && <TableBodyScrollSlot />}
      {!loading && !hasData && <TableBodyEmptySlot />}
    </div>
  );
};

export default VInnerBaseTableTbody;
